import { media } from '@/styles/Qtheme';
import { useIsMobile } from '@/utils/CalVW';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useState } from 'react';

interface RewardButtonProps {
  missionId: number;
  canClaimable: boolean; // 보상 수령 가능 여부
  getMissionStatus: string | undefined; // 미션 상태
  handleClaim: (missionId: number) => void; // 보상 수령 핸들러
  notCompletedComponent?: React.ReactNode; // 완료 되지 않았을때 보여줄 컴포넌트
  style?: React.CSSProperties;
}

export const RewardButton = ({
  missionId,
  canClaimable,
  getMissionStatus,
  handleClaim,
  notCompletedComponent,
  style,
}: RewardButtonProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <>
      {getMissionStatus === 'claimed' ? (
        <ClaimedRewardButton style={style} disabled isMobile={isMobile}>
          <svg
            width="18"
            height="19"
            viewBox="0 0 18 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.1875 4.4375H14.1586C14.3677 4.13925 14.4795 3.78364 14.4788 3.41938C14.4786 3.18262 14.4314 2.94825 14.3399 2.72989C14.2483 2.51154 14.1143 2.31355 13.9456 2.14745C13.6223 1.82915 13.191 1.64422 12.7376 1.62946C12.2841 1.6147 11.8417 1.77119 11.4984 2.0678L9 4.25272L6.50143 2.06774C6.15767 1.77088 5.71467 1.61445 5.26072 1.62961C4.80677 1.64478 4.3752 1.83044 4.05202 2.14959C3.75263 2.44479 3.56725 2.83634 3.52865 3.25501C3.49004 3.67369 3.60071 4.09253 3.84109 4.4375H2.8125C2.36495 4.4375 1.93572 4.61529 1.61926 4.93176C1.30279 5.24822 1.125 5.67745 1.125 6.125C1.125 6.57255 1.30279 7.00178 1.61926 7.31824C1.93572 7.63471 2.36495 7.8125 2.8125 7.8125H15.1875C15.6351 7.8125 16.0643 7.63471 16.3807 7.31824C16.6972 7.00178 16.875 6.57255 16.875 6.125C16.875 5.67745 16.6972 5.24822 16.3807 4.93176C16.0643 4.61529 15.6351 4.4375 15.1875 4.4375ZM12.239 2.91481C12.3668 2.80275 12.5324 2.74355 12.7022 2.74927C12.872 2.75498 13.0332 2.82518 13.1531 2.94558C13.2167 3.00708 13.2672 3.08074 13.3018 3.16218C13.3363 3.24362 13.3541 3.33117 13.3541 3.41963C13.3541 3.50808 13.3363 3.59564 13.3018 3.67708C13.2672 3.75851 13.2167 3.83218 13.1531 3.89367L12.609 4.4375H10.4978L12.239 2.91481ZM4.84251 3.88901C4.78009 3.82749 4.73056 3.75414 4.69682 3.67326C4.66308 3.59237 4.64581 3.50557 4.64601 3.41793C4.64622 3.33029 4.66389 3.24357 4.69801 3.16284C4.73212 3.08212 4.78199 3.009 4.8447 2.94778C4.96435 2.82635 5.12592 2.75529 5.29628 2.74917C5.46664 2.74304 5.63289 2.8023 5.76096 2.91481L7.50223 4.4375H5.391L4.84251 3.88901ZM8.43187 8.9375L8.42063 17.375H4.5C4.2045 17.3751 3.91189 17.3169 3.63887 17.2039C3.36585 17.0908 3.11778 16.9251 2.90883 16.7162C2.69988 16.5072 2.53415 16.2592 2.42111 15.9861C2.30806 15.7131 2.24992 15.4205 2.25 15.125V8.88125C2.43496 8.92 2.62353 8.93886 2.8125 8.9375H8.43187ZM15.75 8.88125V15.125C15.7501 15.4205 15.6919 15.7131 15.5789 15.9861C15.4658 16.2592 15.3001 16.5072 15.0912 16.7162C14.8822 16.9251 14.6342 17.0908 14.3611 17.2039C14.0881 17.3169 13.7955 17.3751 13.5 17.375H9.54562L9.55688 8.9375H15.1875C15.3765 8.93886 15.565 8.92 15.75 8.88125Z"
              fill="white"
            />
          </svg>
          {t('Rewarded')}
        </ClaimedRewardButton>
      ) : !canClaimable ? (
        <>{notCompletedComponent || <span>{t('Not Completed')}</span>}</>
      ) : (
        <ClaimRewardButton
          style={style}
          onClick={() => handleClaim(missionId)}
          isMobile={isMobile}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <motion.svg
            animate={isHovered ? {
              rotate: [-12, 12],
            } : { rotate: 0 }}
            transition={{
              duration: 0.2,
              repeat: isHovered ? Infinity : 0,
              repeatType: "reverse",
              ease: "easeInOut"
            }}
            width="18"
            height="19"
            viewBox="0 0 18 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.1875 4.4375H14.1586C14.3677 4.13925 14.4795 3.78364 14.4788 3.41938C14.4786 3.18262 14.4314 2.94825 14.3399 2.72989C14.2483 2.51154 14.1143 2.31355 13.9456 2.14745C13.6223 1.82915 13.191 1.64422 12.7376 1.62946C12.2841 1.6147 11.8417 1.77119 11.4984 2.0678L9 4.25272L6.50143 2.06774C6.15767 1.77088 5.71467 1.61445 5.26072 1.62961C4.80677 1.64478 4.3752 1.83044 4.05202 2.14959C3.75263 2.44479 3.56725 2.83634 3.52865 3.25501C3.49004 3.67369 3.60071 4.09253 3.84109 4.4375H2.8125C2.36495 4.4375 1.93572 4.61529 1.61926 4.93176C1.30279 5.24822 1.125 5.67745 1.125 6.125C1.125 6.57255 1.30279 7.00178 1.61926 7.31824C1.93572 7.63471 2.36495 7.8125 2.8125 7.8125H15.1875C15.6351 7.8125 16.0643 7.63471 16.3807 7.31824C16.6972 7.00178 16.875 6.57255 16.875 6.125C16.875 5.67745 16.6972 5.24822 16.3807 4.93176C16.0643 4.61529 15.6351 4.4375 15.1875 4.4375ZM12.239 2.91481C12.3668 2.80275 12.5324 2.74355 12.7022 2.74927C12.872 2.75498 13.0332 2.82518 13.1531 2.94558C13.2167 3.00708 13.2672 3.08074 13.3018 3.16218C13.3363 3.24362 13.3541 3.33117 13.3541 3.41963C13.3541 3.50808 13.3363 3.59564 13.3018 3.67708C13.2672 3.75851 13.2167 3.83218 13.1531 3.89367L12.609 4.4375H10.4978L12.239 2.91481ZM4.84251 3.88901C4.78009 3.82749 4.73056 3.75414 4.69682 3.67326C4.66308 3.59237 4.64581 3.50557 4.64601 3.41793C4.64622 3.33029 4.66389 3.24357 4.69801 3.16284C4.73212 3.08212 4.78199 3.009 4.8447 2.94778C4.96435 2.82635 5.12592 2.75529 5.29628 2.74917C5.46664 2.74304 5.63289 2.8023 5.76096 2.91481L7.50223 4.4375H5.391L4.84251 3.88901ZM8.43187 8.9375L8.42063 17.375H4.5C4.2045 17.3751 3.91189 17.3169 3.63887 17.2039C3.36585 17.0908 3.11778 16.9251 2.90883 16.7162C2.69988 16.5072 2.53415 16.2592 2.42111 15.9861C2.30806 15.7131 2.24992 15.4205 2.25 15.125V8.88125C2.43496 8.92 2.62353 8.93886 2.8125 8.9375H8.43187ZM15.75 8.88125V15.125C15.7501 15.4205 15.6919 15.7131 15.5789 15.9861C15.4658 16.2592 15.3001 16.5072 15.0912 16.7162C14.8822 16.9251 14.6342 17.0908 14.3611 17.2039C14.0881 17.3169 13.7955 17.3751 13.5 17.375H9.54562L9.55688 8.9375H15.1875C15.3765 8.93886 15.565 8.92 15.75 8.88125Z"
              fill="white"
            />
          </motion.svg>
          {t('Claim Reward')}
        </ClaimRewardButton>
      )}
    </>
  );
};

export default RewardButton;

const ClaimedRewardButton = styled.button<{
  disabled: boolean;
  isMobile: boolean;
}>`
  align-self: flex-start;
  width: ${({ isMobile }) => (isMobile ? '100%' : '116px')};
  height: ${({ isMobile }) => (isMobile ? '32px' : '46px')};
  border-radius: 4px;
  border: none;
  background: #474747;
  color: #b5b5bc;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  font-weight: 700;
  font-family: 'Pretendard', sans-serif;
  white-space: nowrap;
  font-size: 16px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  transition: filter 0.2s ease;

  &:hover {
    filter: ${({ disabled }) => (disabled ? 'none' : 'brightness(1.1)')};
  }

  @media ${media.mobile} {
    font-size: 14px;
  }
`;

const ClaimRewardButton = styled.button<{ isMobile: boolean }>`
  align-self: flex-start;
  height: ${({ isMobile }) => (isMobile ? '32px' : '46px')};
  border-radius: 4px;
  border: none;
  background: #ff5c17;
  color: #fff;
  cursor: pointer;
  font-weight: 700;
  font-family: 'Pretendard', sans-serif;
  font-size: 16px;
  white-space: nowrap;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }

  @media ${media.mobile} {
    font-size: 14px;
  }
`;
